import { getUTCOffset } from './location';

const HOUR = 3600000;

/**
 * Resolve a stored profile's birth moment WITHOUT depending on the device's
 * current timezone.
 *
 * Background: profiles historically stored birth_date as toISOString() of the
 * registration device's picker Date. Reading that instant back with local
 * getters (getHours() etc.) re-renders it in the CURRENT device's timezone, so
 * the natal chart shifted whenever the user travelled (e.g. Dublin -> Romania
 * moved the ascendant two hours late). This helper reconstructs the birth
 * wall-clock (what the user actually typed) from the stored fields alone.
 *
 * Input shapes handled:
 * 1. Date-only birth_date "YYYY-MM-DD" (BirthDataWizard / cosmicAPI): the
 *    string IS the civil date; combined with birth_time, or the noon
 *    convention when the time is unknown.
 * 2. Full ISO instant + birth_time: birth_time is authoritative for the time
 *    of day (captured with toTimeString() on the entry device, so it is
 *    exactly what the user entered, on any device). The civil date is
 *    recovered by trying the instant's birth-timezone date and its two
 *    neighbours: an exact (±30 min) instant match wins (new-format rows and
 *    same-zone registrations); otherwise legacy rows are disambiguated by
 *    requiring the implied registration-device offset (birth offset minus the
 *    stored-vs-candidate delta) to be a plausible inhabited zone (UTC-10 to
 *    UTC+13).
 * 3. Full ISO instant without birth_time: the instant is trusted as the true
 *    UTC moment.
 *
 * @returns {{
 *   civil: { year, month, day, hour, minute, second },  // month 1-12, birth-place wall-clock
 *   utc: Date,       // true UTC instant of birth — feed to ephemeris math directly
 *   localDate: Date, // Date whose device-local wall-clock equals the birth wall-clock
 * } | null}
 *
 * Prefer `utc` for calculations. `localDate` exists for the wheel/calendar
 * components' legacy natalDate prop and for display; note it is best-effort if
 * the birth wall-clock happens to fall inside the VIEWING device zone's DST
 * spring-forward gap (JS shifts nonexistent local times by an hour).
 */
export function resolveBirthMoment(profile) {
  if (!profile) return null;

  const birthDateRaw = profile.birth_date || profile.birthDate;
  if (!birthDateRaw) return null;
  const birthTimeStr = profile.birth_time || profile.birthTime || null;
  const timezone = profile.birth_timezone || profile.timezone || null;

  const timeParts = typeof birthTimeStr === 'string' && /^\d{1,2}:\d{2}/.test(birthTimeStr)
    ? birthTimeStr.split(':').map(Number)
    : null;

  // Shape 1: date-only string — the civil date is written down directly.
  if (typeof birthDateRaw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(birthDateRaw.trim())) {
    const [year, month, day] = birthDateRaw.trim().split('-').map(Number);
    const [hour, minute, second = 0] = timeParts || [12, 0, 0]; // noon convention for unknown time
    return buildResult({ year, month, day, hour, minute, second }, civilToUTC(year, month, day, hour, minute, second, timezone));
  }

  const storedInstant = birthDateRaw instanceof Date ? birthDateRaw : new Date(birthDateRaw);
  if (isNaN(storedInstant.getTime())) return null;

  // Shape 3: no wall-clock string stored — the instant itself is the best information.
  if (!timeParts) {
    return buildResult(getCivilInTimeZone(storedInstant, timezone), storedInstant.getTime());
  }

  // Shape 2: recover the entered civil date around the stored instant.
  const [hour, minute, second = 0] = timeParts;
  const base = getCivilInTimeZone(storedInstant, timezone);

  const candidates = [-1, 0, 1].map(shift => {
    const dayUTC = new Date(Date.UTC(base.year, base.month - 1, base.day + shift));
    const epoch = civilToUTC(dayUTC.getUTCFullYear(), dayUTC.getUTCMonth() + 1, dayUTC.getUTCDate(), hour, minute, second, timezone);
    // deltaMs = storedInstant - trueEpoch = birthOffset - registrationDeviceOffset (for the right date)
    return { dayUTC, epoch, deltaMs: storedInstant.getTime() - epoch };
  });

  // (a) exact/near-exact instant match: new-format rows and same-zone registrations
  let best = candidates.find(c => Math.abs(c.deltaMs) <= 0.5 * HOUR);

  if (!best) {
    // (b) legacy rows: keep candidates whose implied registration-device offset
    // lands in an inhabited zone (UTC-10 Honolulu .. UTC+13 NZDT, ±30min slack)
    const birthOffsetH = timezone ? getUTCOffset(storedInstant, timezone) : 0;
    const plausible = candidates.filter(c => {
      const impliedRegOffset = birthOffsetH - c.deltaMs / HOUR;
      return impliedRegOffset >= -10.5 && impliedRegOffset <= 13.5;
    });
    const pool = plausible.length ? plausible : candidates;
    best = pool.reduce((a, b) => (Math.abs(b.deltaMs) < Math.abs(a.deltaMs) ? b : a));
  }

  return buildResult({
    year: best.dayUTC.getUTCFullYear(),
    month: best.dayUTC.getUTCMonth() + 1,
    day: best.dayUTC.getUTCDate(),
    hour, minute, second,
  }, best.epoch);
}

function buildResult(civil, utcMs) {
  return {
    civil,
    utc: new Date(utcMs),
    localDate: new Date(civil.year, civil.month - 1, civil.day, civil.hour, civil.minute, civil.second),
  };
}

/**
 * True UTC epoch (ms) for a civil wall-clock in a given IANA timezone.
 * Two-pass offset lookup so DST at the birth moment itself is honoured.
 * Month is 1-12. Falls back to treating the wall-clock as UTC when the
 * timezone is missing.
 */
export function civilToUTC(year, month, day, hour, minute, second, timezone) {
  const guess = Date.UTC(year, month - 1, day, hour, minute, second);
  if (!timezone) return guess;
  let offset = getUTCOffset(new Date(guess), timezone);
  offset = getUTCOffset(new Date(guess - offset * HOUR), timezone);
  return guess - offset * HOUR;
}

/**
 * Civil (wall-clock) components of an instant in a given IANA timezone,
 * independent of the device timezone. Falls back to UTC components when the
 * timezone is missing or unknown. Month is 1-12.
 */
export function getCivilInTimeZone(date, timezone) {
  if (timezone) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(date);
      const get = (type) => Number(parts.find(p => p.type === type)?.value);
      const civil = {
        year: get('year'), month: get('month'), day: get('day'),
        hour: get('hour') % 24, minute: get('minute'), second: get('second'),
      };
      if (Object.values(civil).every(v => !isNaN(v))) return civil;
    } catch (e) {
      // fall through to UTC
    }
  }
  return {
    year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate(),
    hour: date.getUTCHours(), minute: date.getUTCMinutes(), second: date.getUTCSeconds(),
  };
}
